import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { X, Plus, Trash2, FileText, Download, Upload, Plane, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ROOM_TYPES = ['Standard', 'Deluxe', 'Super Deluxe', 'Suite', 'Premium', 'Villa', 'Family Room', 'Executive', 'Presidential Suite'];

const fullImg = (u) => (!u ? '' : u.startsWith('http') ? u : `${API_URL}${u}`);
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Compact image field: URL input + thumbnail + upload button
const ImgField = ({ value, onChange, adminAuth }) => {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/admin/upload/blog`, {
        method: 'POST', headers: { Authorization: `Basic ${adminAuth}` }, body: fd,
      });
      if (res.ok) { const d = await res.json(); onChange(d.url); }
      else toast.error('Upload failed');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };
  return (
    <div className="flex items-center gap-2">
      {value ? <img src={fullImg(value)} alt="" className="w-10 h-10 rounded object-cover shrink-0" /> : <div className="w-10 h-10 rounded bg-gray-100 shrink-0" />}
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" className="flex-1 h-9 text-sm" />
      <label className="shrink-0 cursor-pointer text-xs px-2 py-2 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 font-medium">
        <Upload className="w-3 h-3 inline mr-1" />{uploading ? 'Uploading…' : 'Upload photo'}
        <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
      </label>
    </div>
  );
};

const ItemRow = ({ item, onChange, onRemove, adminAuth }) => (
  <div className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
    <div className="flex gap-2">
      <Input value={item.name} onChange={(e) => onChange({ ...item, name: e.target.value })} placeholder="Activity name" className="flex-1 h-9 text-sm" />
      <Input type="number" min="0" value={item.price} onChange={(e) => onChange({ ...item, price: parseInt(e.target.value) || 0 })} placeholder="Price ₹" className="w-28 h-9 text-sm" />
      <button onClick={onRemove} className="p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
    </div>
    <Input value={item.description} onChange={(e) => onChange({ ...item, description: e.target.value })} placeholder="Short description" className="h-9 text-sm" />
    <ImgField value={item.image} onChange={(url) => onChange({ ...item, image: url })} adminAuth={adminAuth} />
  </div>
);

const HotelRow = ({ hotel, onChange, onRemove, adminAuth }) => (
  <div className="border border-gray-200 rounded-lg p-3 space-y-2 bg-blue-50/40">
    <div className="flex gap-2">
      <Input value={hotel.name} onChange={(e) => onChange({ ...hotel, name: e.target.value })} placeholder="Hotel name" className="flex-1 h-9 text-sm" />
      {onRemove && <button onClick={onRemove} className="p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>}
    </div>
    <div className="flex gap-2">
      <input list="room-types" value={hotel.room_type} onChange={(e) => onChange({ ...hotel, room_type: e.target.value })} placeholder="Room type (pick or type)" className="flex-1 h-9 text-sm border border-gray-300 rounded-md px-3 bg-white" />
      <Input type="number" min="1" value={hotel.nights} onChange={(e) => onChange({ ...hotel, nights: parseInt(e.target.value) || 1 })} placeholder="Nights" className="w-20 h-9 text-sm" />
      <Input type="number" min="0" value={hotel.price} onChange={(e) => onChange({ ...hotel, price: parseInt(e.target.value) || 0 })} placeholder="₹/night" className="w-28 h-9 text-sm" />
    </div>
    <ImgField value={hotel.image} onChange={(url) => onChange({ ...hotel, image: url })} adminAuth={adminAuth} />
  </div>
);

const blankItem = () => ({ name: '', description: '', image: '', price: 0 });
const blankHotel = () => ({ name: '', image: '', room_type: 'Deluxe', nights: 1, price: 0 });
const blankFlight = () => ({ name: '', route: '', baggage: '', layover: '', price: 0 });

const FlightRow = ({ flight, onChange, onRemove }) => (
  <div className="border border-gray-200 rounded-lg p-3 space-y-2 bg-sky-50/50">
    <div className="flex gap-2">
      <Input value={flight.name} onChange={(e) => onChange({ ...flight, name: e.target.value })} placeholder="Flight name / number (e.g. AI 332)" className="flex-1 h-9 text-sm" />
      <Input type="number" min="0" value={flight.price} onChange={(e) => onChange({ ...flight, price: parseInt(e.target.value) || 0 })} placeholder="Price ₹" className="w-28 h-9 text-sm" />
      <button onClick={onRemove} className="p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
    </div>
    <div className="flex gap-2">
      <Input value={flight.route} onChange={(e) => onChange({ ...flight, route: e.target.value })} placeholder="Destinations (e.g. DEL → DPS)" className="flex-1 h-9 text-sm" />
      <Input value={flight.baggage} onChange={(e) => onChange({ ...flight, baggage: e.target.value })} placeholder="Baggage (e.g. 30kg)" className="w-36 h-9 text-sm" />
    </div>
    <Input value={flight.layover} onChange={(e) => onChange({ ...flight, layover: e.target.value })} placeholder="Layover (optional, e.g. 2h at Singapore)" className="h-9 text-sm" />
  </div>
);

const ItineraryBuilder = ({ inquiryId, inquiry, adminAuth, onClose, onSent, initialSavedId }) => {
  const [title, setTitle] = useState('Your Travel Itinerary');
  const [intro, setIntro] = useState('');
  const [destination, setDestination] = useState(inquiry?.destination || '');
  const [travelDates, setTravelDates] = useState('');
  const [layout, setLayout] = useState('sections');
  const [activities, setActivities] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [days, setDays] = useState([{ title: '', items: [], hotel: null }]);
  const [includeFlights, setIncludeFlights] = useState(false);
  const [flights, setFlights] = useState([]);
  const [includeVisa, setIncludeVisa] = useState(false);
  const [visaLabel, setVisaLabel] = useState('');
  const [visaPrice, setVisaPrice] = useState(0);
  const [includeInsurance, setIncludeInsurance] = useState(false);
  const [insuranceLabel, setInsuranceLabel] = useState('');
  const [insurancePrice, setInsurancePrice] = useState(0);
  const [visaList, setVisaList] = useState([]);
  const [taxPercent, setTaxPercent] = useState(5);
  const [taxLabel, setTaxLabel] = useState('GST');
  const [discount, setDiscount] = useState(0);
  const [terms, setTerms] = useState('');
  const [note, setNote] = useState('');
  const [destList, setDestList] = useState([]);
  const [importDest, setImportDest] = useState('');
  const [savedList, setSavedList] = useState([]);
  const [loadSavedId, setLoadSavedId] = useState('');
  const [sending, setSending] = useState(false);

  const init = useCallback(async () => {
    try {
      const [s, d, saved, visas] = await Promise.all([
        fetch(`${API_URL}/api/settings`).then((r) => r.json()),
        fetch(`${API_URL}/api/destinations`).then((r) => r.json()),
        fetch(`${API_URL}/api/admin/saved-itineraries`, { headers: { Authorization: `Basic ${adminAuth}` } }).then((r) => (r.ok ? r.json() : [])),
        fetch(`${API_URL}/api/visas`).then((r) => (r.ok ? r.json() : [])),
      ]);
      setTerms(s.default_terms || '');
      setTaxPercent(s.default_tax_percent ?? 5);
      setTaxLabel(s.tax_label || 'GST');
      setDestList(d || []);
      setVisaList(visas || []);
      setSavedList(saved || []);
    } catch { /* ignore */ }
  }, [adminAuth]);

  useEffect(() => { init(); }, [init]);

  const loadSavedById = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/saved-itineraries/${id}`, { headers: { Authorization: `Basic ${adminAuth}` } });
      if (!res.ok) { toast.error('Failed to load'); return; }
      const d = await res.json();
      const p = d.payload || {};
      setTitle(p.title || 'Your Travel Itinerary');
      setIntro(p.intro || '');
      setDestination(p.destination || destination);
      setTravelDates(p.travel_dates || '');
      setLayout(p.layout || 'sections');
      setActivities(p.activities || []);
      setHotels(p.hotels || []);
      setDays(p.days && p.days.length ? p.days : [{ title: '', items: [], hotel: null }]);
      setIncludeFlights(!!p.include_flights);
      setFlights(p.flights || []);
      setIncludeVisa(!!p.include_visa);
      setVisaLabel(p.visa_label || '');
      setVisaPrice(p.visa_price || 0);
      setIncludeInsurance(!!p.include_insurance);
      setInsuranceLabel(p.insurance_label || '');
      setInsurancePrice(p.insurance_price || 0);
      setTaxPercent(p.tax_percent ?? 5);
      setTaxLabel(p.tax_label || 'GST');
      setDiscount(p.discount || 0);
      setTerms(p.terms || '');
      toast.success('Loaded — edit dates/hotels/activities and send');
    } catch { toast.error('Failed to load'); }
  };

  const loadSaved = () => {
    if (!loadSavedId) { toast.error('Select a saved itinerary'); return; }
    loadSavedById(loadSavedId);
  };

  useEffect(() => {
    if (initialSavedId) loadSavedById(initialSavedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSavedId]);

  const importActivities = () => {
    const dest = destList.find((x) => String(x.id) === String(importDest));
    if (!dest) { toast.error('Select a destination to import from'); return; }
    const imported = (dest.activities || []).map((a) => ({
      name: a.name, description: a.description, image: a.image, price: a.price || 0,
    }));
    setActivities((prev) => [...prev, ...imported]);
    if (!destination) setDestination(dest.name);
    toast.success(`${imported.length} activities imported`);
  };

  // pricing preview
  let subtotal = 0;
  if (layout === 'days') {
    days.forEach((d) => {
      (d.items || []).forEach((it) => { subtotal += it.price || 0; });
      if (d.hotel) subtotal += (d.hotel.price || 0) * (d.hotel.nights || 1);
    });
  } else {
    activities.forEach((it) => { subtotal += it.price || 0; });
    hotels.forEach((h) => { subtotal += (h.price || 0) * (h.nights || 1); });
  }
  if (includeFlights) {
    flights.forEach((f) => { subtotal += f.price || 0; });
  }
  if (includeVisa) subtotal += visaPrice || 0;
  if (includeInsurance) subtotal += insurancePrice || 0;
  const after = Math.max(subtotal - (discount || 0), 0);
  const taxAmt = Math.round((after * (taxPercent || 0)) / 100);
  const grand = after + taxAmt;

  // day helpers
  const updateDay = (i, patch) => setDays(days.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const addDay = () => setDays([...days, { title: '', items: [], hotel: null }]);
  const removeDay = (i) => setDays(days.filter((_, idx) => idx !== i));

  const send = async () => {
    setSending(true);
    try {
      const payload = {
        title, layout, intro, destination, travel_dates: travelDates,
        activities: layout === 'sections' ? activities : [],
        hotels: layout === 'sections' ? hotels.filter((h) => h.name) : [],
        days: layout === 'days' ? days.map((d) => ({
          title: d.title,
          items: d.items || [],
          hotel: d.hotel && d.hotel.name ? d.hotel : null,
        })) : [],
        include_flights: includeFlights,
        flights: includeFlights ? flights.filter((f) => f.name || f.route) : [],
        include_visa: includeVisa,
        visa_label: visaLabel, visa_price: visaPrice || 0,
        include_insurance: includeInsurance,
        insurance_label: insuranceLabel, insurance_price: insurancePrice || 0,
        tax_percent: taxPercent, tax_label: taxLabel, discount: discount || 0,
        terms, note,
      };
      const res = await fetch(`${API_URL}/api/admin/inquiries/${inquiryId}/itinerary-pdf`, {
        method: 'POST',
        headers: { Authorization: `Basic ${adminAuth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Itinerary PDF generated & sent to client');
        if (onSent) onSent();
        onClose();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to generate itinerary');
      }
    } catch { toast.error('Connection error'); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="itinerary-builder-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><FileText className="w-5 h-5 text-amber-600" /> Build Itinerary PDF</h2>
          <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded" data-testid="close-builder-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <datalist id="room-types">{ROOM_TYPES.map((r) => <option key={r} value={r} />)}</datalist>

          {/* Reuse a saved itinerary */}
          {savedList.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 bg-blue-50 p-3 rounded-lg" data-testid="load-saved-bar">
              <FolderOpen className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-gray-700">Reuse saved:</span>
              <select value={loadSavedId} onChange={(e) => setLoadSavedId(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white flex-1 min-w-[180px]" data-testid="load-saved-select">
                <option value="">Choose a saved itinerary…</option>
                {savedList.map((s) => <option key={s.id} value={s.id}>{s.title} — {s.client_name || 'client'}</option>)}
              </select>
              <Button size="sm" variant="outline" onClick={loadSaved} data-testid="load-saved-btn">Load</Button>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium mb-1">Title</label>
              <Input data-testid="builder-title" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
            <div><label className="block text-sm font-medium mb-1">Destination</label>
              <Input data-testid="builder-destination" value={destination} onChange={(e) => setDestination(e.target.value)} /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium mb-1">Travel Dates</label>
              <Input data-testid="builder-dates" value={travelDates} onChange={(e) => setTravelDates(e.target.value)} placeholder="e.g. 12 - 18 Jan 2026" /></div>
          </div>
          <div><label className="block text-sm font-medium mb-1">Intro / Welcome note</label>
            <Textarea rows={2} value={intro} onChange={(e) => setIntro(e.target.value)} placeholder="A short intro shown at the top of the PDF" /></div>

          {/* Flights */}
          <div className="border border-gray-200 rounded-xl p-3">
            <label className="flex items-center gap-2 text-sm font-medium mb-2">
              <input type="checkbox" checked={includeFlights} onChange={(e) => setIncludeFlights(e.target.checked)} data-testid="include-flights-toggle" />
              <Plane className="w-4 h-4 text-sky-600" /> Include flights
            </label>
            {includeFlights && (
              <div className="space-y-2">
                {flights.map((f, i) => (
                  <FlightRow key={i} flight={f}
                    onChange={(v) => setFlights(flights.map((x, idx) => (idx === i ? v : x)))}
                    onRemove={() => setFlights(flights.filter((_, idx) => idx !== i))} />
                ))}
                <Button size="sm" variant="outline" onClick={() => setFlights([...flights, blankFlight()])} data-testid="add-flight-btn"><Plus className="w-4 h-4 mr-1" />Add Flight</Button>
              </div>
            )}
          </div>

          {/* Visa & Insurance */}
          <div className="border border-gray-200 rounded-xl p-3 space-y-3">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2">
                <input type="checkbox" checked={includeVisa} onChange={(e) => setIncludeVisa(e.target.checked)} data-testid="include-visa-toggle" />
                Include Visa
              </label>
              {includeVisa && (
                <div className="space-y-2">
                  {visaList.length > 0 && (
                    <select
                      data-testid="visa-picker"
                      className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white w-full"
                      onChange={(e) => {
                        const v = visaList.find((x) => x.id === e.target.value);
                        if (v) { setVisaLabel(`${v.country} Visa`); setVisaPrice(v.price || 0); }
                      }}
                      defaultValue=""
                    >
                      <option value="">Pick from visa list (auto-fills)…</option>
                      {visaList.map((v) => <option key={v.id} value={v.id}>{v.country} — ₹{Number(v.price).toLocaleString('en-IN')}</option>)}
                    </select>
                  )}
                  <div className="flex gap-2">
                    <Input value={visaLabel} onChange={(e) => setVisaLabel(e.target.value)} placeholder="Visa label (e.g. Vietnam e-Visa)" className="flex-1 h-9 text-sm" data-testid="visa-label-input" />
                    <Input type="number" min="0" value={visaPrice} onChange={(e) => setVisaPrice(parseInt(e.target.value) || 0)} placeholder="Price ₹" className="w-28 h-9 text-sm" data-testid="visa-price-input" />
                  </div>
                </div>
              )}
            </div>
            <div className="border-t pt-3">
              <label className="flex items-center gap-2 text-sm font-medium mb-2">
                <input type="checkbox" checked={includeInsurance} onChange={(e) => setIncludeInsurance(e.target.checked)} data-testid="include-insurance-toggle" />
                Include Travel Insurance
              </label>
              {includeInsurance && (
                <div className="flex gap-2">
                  <Input value={insuranceLabel} onChange={(e) => setInsuranceLabel(e.target.value)} placeholder="Insurance label (e.g. Travel Insurance, 5 days)" className="flex-1 h-9 text-sm" data-testid="insurance-label-input" />
                  <Input type="number" min="0" value={insurancePrice} onChange={(e) => setInsurancePrice(parseInt(e.target.value) || 0)} placeholder="Price ₹" className="w-28 h-9 text-sm" data-testid="insurance-price-input" />
                </div>
              )}
            </div>
          </div>

          {/* Layout toggle */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Layout:</span>
            {['sections', 'days'].map((l) => (
              <button key={l} data-testid={`layout-${l}`} onClick={() => setLayout(l)}
                className={`px-3 py-1.5 rounded-full text-sm ${layout === l ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
                {l === 'sections' ? 'Sectioned (Activities + Hotels)' : 'Day-by-day'}
              </button>
            ))}
          </div>

          {layout === 'sections' ? (
            <>
              <div className="flex flex-wrap items-center gap-2 bg-amber-50 p-3 rounded-lg">
                <span className="text-sm text-gray-700">Quick import:</span>
                <select value={importDest} onChange={(e) => setImportDest(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white" data-testid="import-dest-select">
                  <option value="">Choose destination…</option>
                  {destList.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <Button size="sm" variant="outline" onClick={importActivities} data-testid="import-activities-btn">Import its activities</Button>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-800">Activities &amp; Experiences</h3>
                  <Button size="sm" variant="outline" onClick={() => setActivities([...activities, blankItem()])} data-testid="add-activity-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
                </div>
                <div className="space-y-2">
                  {activities.map((it, i) => (
                    <ItemRow key={i} item={it} adminAuth={adminAuth}
                      onChange={(v) => setActivities(activities.map((x, idx) => (idx === i ? v : x)))}
                      onRemove={() => setActivities(activities.filter((_, idx) => idx !== i))} />
                  ))}
                  {activities.length === 0 && <p className="text-sm text-gray-400">No activities yet.</p>}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-800">Hotels &amp; Stay</h3>
                  <Button size="sm" variant="outline" onClick={() => setHotels([...hotels, blankHotel()])} data-testid="add-hotel-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
                </div>
                <div className="space-y-2">
                  {hotels.map((h, i) => (
                    <HotelRow key={i} hotel={h} adminAuth={adminAuth}
                      onChange={(v) => setHotels(hotels.map((x, idx) => (idx === i ? v : x)))}
                      onRemove={() => setHotels(hotels.filter((_, idx) => idx !== i))} />
                  ))}
                  {hotels.length === 0 && <p className="text-sm text-gray-400">No hotels yet.</p>}
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              {days.map((d, di) => (
                <div key={di} className="border border-gray-200 rounded-xl p-3" data-testid={`day-block-${di}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-bold text-amber-700">Day {di + 1}</span>
                    <Input value={d.title} onChange={(e) => updateDay(di, { title: e.target.value })} placeholder="Day title (e.g. Arrival & Beach)" className="flex-1 h-9 text-sm" />
                    <button onClick={() => removeDay(di)} className="p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="space-y-2 mb-2">
                    {(d.items || []).map((it, ii) => (
                      <ItemRow key={ii} item={it} adminAuth={adminAuth}
                        onChange={(v) => updateDay(di, { items: d.items.map((x, idx) => (idx === ii ? v : x)) })}
                        onRemove={() => updateDay(di, { items: d.items.filter((_, idx) => idx !== ii) })} />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => updateDay(di, { items: [...(d.items || []), blankItem()] })}><Plus className="w-3 h-3 mr-1" />Activity</Button>
                    {!d.hotel
                      ? <Button size="sm" variant="outline" onClick={() => updateDay(di, { hotel: blankHotel() })}><Plus className="w-3 h-3 mr-1" />Hotel</Button>
                      : null}
                  </div>
                  {d.hotel && (
                    <div className="mt-2">
                      <HotelRow hotel={d.hotel} adminAuth={adminAuth}
                        onChange={(v) => updateDay(di, { hotel: v })}
                        onRemove={() => updateDay(di, { hotel: null })} />
                    </div>
                  )}
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addDay} data-testid="add-day-btn"><Plus className="w-4 h-4 mr-1" />Add Day</Button>
            </div>
          )}

          {/* Pricing */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Pricing</h3>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div><label className="block text-xs text-gray-600 mb-1">Discount ₹</label>
                <Input type="number" min="0" value={discount} onChange={(e) => setDiscount(parseInt(e.target.value) || 0)} className="h-9 text-sm" /></div>
              <div><label className="block text-xs text-gray-600 mb-1">Tax label</label>
                <Input value={taxLabel} onChange={(e) => setTaxLabel(e.target.value)} className="h-9 text-sm" /></div>
              <div><label className="block text-xs text-gray-600 mb-1">Tax %</label>
                <Input type="number" min="0" step="0.5" value={taxPercent} onChange={(e) => setTaxPercent(parseFloat(e.target.value) || 0)} className="h-9 text-sm" /></div>
            </div>
            <div className="text-sm space-y-1" data-testid="pricing-preview">
              <div className="flex justify-between"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-gray-500"><span>Discount</span><span>- {inr(discount)}</span></div>}
              {taxAmt > 0 && <div className="flex justify-between text-gray-500"><span>{taxLabel} ({taxPercent}%)</span><span>{inr(taxAmt)}</span></div>}
              <div className="flex justify-between font-bold text-gray-900 text-base pt-1 border-t"><span>Grand Total</span><span>{inr(grand)}</span></div>
            </div>
          </div>

          <div><label className="block text-sm font-medium mb-1">Terms &amp; Conditions</label>
            <Textarea data-testid="builder-terms" rows={6} value={terms} onChange={(e) => setTerms(e.target.value)} /></div>
          <div><label className="block text-sm font-medium mb-1">Message to client (optional)</label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="A short note shown with the PDF in the chat" /></div>
        </div>

        <div className="border-t p-4 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending} data-testid="generate-send-pdf-btn" className="bg-amber-600 hover:bg-amber-700">
            <Download className="w-4 h-4 mr-2" />{sending ? 'Generating…' : 'Generate & Send PDF'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ItineraryBuilder;
